import { useMemo, useState, useEffect } from 'react';
import { createFileRoute } from "@tanstack/react-router";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from "@mui/lab";
import {
  Typography,
  Paper,
  Box,
  Chip,
  Skeleton,
  useTheme,
  useMediaQuery
} from "@mui/material";
import {
  Stop,
  Task,
  Person,
  Settings,
  CallSplit,
  AccessTime,
  RocketLaunch
} from "@mui/icons-material";
import {
  getGetProcessActivitiesHistoricQueryOptions,
  getGetProcessInstanceByIdIgnoringTenantQueryOptions
} from "../../../../../api/bpm/endpoints";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

export const Route = createFileRoute('/__auth/processes/$processInstanceId/history/timeline')({
  component: TimelinePage,
  staticData: {
    title: "routes.processTimeline",
  },
});

const ACTIVITY_CATEGORIES = {
  'Tarefas do Usuário': ['userTask'],
  'Automação': ['serviceTask', 'scriptTask', 'sendTask', 'receiveTask', 'businessRuleTask'],
  'Eventos': ['startEvent', 'endEvent', 'intermediateCatchEvent', 'intermediateThrowEvent', 'boundaryEvent', 'manualTermination'],
  'Gateways': ['exclusiveGateway', 'parallelGateway', 'inclusiveGateway', 'eventBasedGateway']
};

const ALL_CATEGORIES = Object.keys(ACTIVITY_CATEGORIES);
const AUTOMATED_TASK_TYPES = new Set(ACTIVITY_CATEGORIES['Automação']);
const EVENT_TYPES = new Set(ACTIVITY_CATEGORIES['Eventos']);
const MANUAL_TERMINATION_TYPE = 'manualTermination';

export function TimelinePage() {
    const { t } = useTranslation();
  const { processInstanceId } = Route.useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedCategories, setSelectedCategories] = useState<string[]>(ALL_CATEGORIES);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(interval);
  }, []);

  const { data: historyResponse, isLoading: isLoadingHistory } =
    useQuery({
      ...getGetProcessActivitiesHistoricQueryOptions(processInstanceId, {
        firstResult: 0,
        maxResults: 200,
        sortBy: "startTime",
        sortOrder: "asc",
      }),
      enabled: !!processInstanceId
    });

  const { data: processInstanceResponse, isLoading: isLoadingProcess } = useQuery({
    ...getGetProcessInstanceByIdIgnoringTenantQueryOptions(processInstanceId),
    enabled: !!processInstanceId,
  });

  const historicActivities = useMemo<any[]>(
    () => (historyResponse as any)?.content || [],
    [historyResponse],
  );
  const processInstance = (processInstanceResponse as any)?.data || processInstanceResponse;

  const activities = useMemo(() => {
    if (!processInstance?.isManualTermination || !processInstance?.endTime) {
      return historicActivities;
    }

    if (historicActivities.some(activity => activity.activityType === MANUAL_TERMINATION_TYPE)) {
      return historicActivities;
    }

    return [
      ...historicActivities,
      {
        id: `${processInstanceId}-${MANUAL_TERMINATION_TYPE}`,
        activityType: MANUAL_TERMINATION_TYPE,
        startTime: processInstance.endTime,
        endTime: processInstance.endTime,
        canceled: true,
        terminatedByFullName: processInstance.terminatedByFullName,
        terminatedByUserId: processInstance.terminatedByUserId,
        terminationJustification: processInstance.terminationJustification,
      },
    ];
  }, [historicActivities, processInstance, processInstanceId]);

  const filteredActivities = useMemo(() => {
    if (selectedCategories.length === ALL_CATEGORIES.length) return activities;

    const allowedTypes = new Set(
      selectedCategories.flatMap(cat => ACTIVITY_CATEGORIES[cat as keyof typeof ACTIVITY_CATEGORIES])
    );

    return activities.filter(activity => {
      const type = activity.activityType || '';
      const category = Object.keys(ACTIVITY_CATEGORIES).find(key =>
        ACTIVITY_CATEGORIES[key as keyof typeof ACTIVITY_CATEGORIES].includes(type)
      );
      return category ? allowedTypes.has(type) : true;
    });
  }, [activities, selectedCategories]);

  if (isLoadingHistory || isLoadingProcess) {
    return (
      <Box sx={{ p: 2 }}>
        {[1, 2, 3].map((i) => (
          <Box key={i} sx={{ display: 'flex', mb: 2 }}>
            <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="40%" />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  const getActivityIcon = (type?: string) => {
    if (!type) return <Task fontSize="small" />;
    if (type === MANUAL_TERMINATION_TYPE) return <Stop fontSize="small" />;
    if (type.toLowerCase().includes('gateway')) return <CallSplit fontSize="small" />;
    if (type.toLowerCase().includes('start')) return <RocketLaunch fontSize="small" />;
    if (type.toLowerCase().includes('end')) return <Stop fontSize="small" />;
    if (type === 'userTask') return <Person fontSize="small" />;
    if (['serviceTask', 'scriptTask'].includes(type)) return <Settings fontSize="small" />;
    return <Task fontSize="small" />;
  };

  const getActivityColor = (type?: string, endTime?: string) => {
    if (!endTime) return "primary";
    if (type === MANUAL_TERMINATION_TYPE) return "error";
    if (type?.toLowerCase().includes('start')) return "primary";
    if (type?.toLowerCase().includes('end')) return "error";
    if (type?.toLowerCase().includes('gateway')) return "warning";
    return "success";
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return { date: '-', time: '-' };
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString("pt-BR", { day: '2-digit', month: 'short' }).replace('.', ''),
      time: date.toLocaleTimeString("pt-BR", { hour: '2-digit', minute: '2-digit' }),
      fullDate: date.toLocaleDateString("pt-BR")
    };
  };

  const toggleCategory = (category: string) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const formatDuration = (millis?: number) => {
    if (millis == null) return null;
    const seconds = Math.floor(millis / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);

    if (months > 0) {
      const remainingDays = days % 30;
      return `${months} mês(es)${remainingDays > 0 ? ` ${remainingDays}d` : ''}`;
    }
    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const getElapsedDuration = (startTime?: string) => {
    if (!startTime) return null;
    const start = new Date(startTime).getTime();
    const diff = now - start;
    return formatDuration(diff);
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 2 }}>
      <Paper elevation={0} sx={{ p: 2, mb: 4, bgcolor: 'background.default', border: 1, borderColor: 'divider', borderRadius: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Filtrar por tipo:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {ALL_CATEGORIES.map(category => {
            const isSelected = selectedCategories.includes(category);
            return (
              <Chip
                key={category}
                label={category}
                onClick={() => toggleCategory(category)}
                variant={isSelected ? "filled" : "outlined"}
                color={isSelected ? "primary" : "default"}
                size={isMobile ? "small" : "medium"}
                sx={{
                  transition: 'all 0.2s',
                  fontWeight: isSelected ? 500 : 400
                }}
              />
            );
          })}
        </Box>
      </Paper>

      {filteredActivities.length === 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, opacity: 0.6 }}>
          <Typography color="text.secondary">Nenhuma atividade encontrada.</Typography>
        </Box>
      ) : (
        <Timeline position="alternate" sx={{ p: 0 }}>
          {filteredActivities.map((activity, index) => {
            const isLast = index === filteredActivities.length - 1;
            const start = formatDateTime(activity.startTime);
            const end = formatDateTime(activity.endTime);

            const isActive = !activity.endTime;
            const duration = isActive
              ? getElapsedDuration(activity.startTime)
              : formatDuration(activity.durationInMillis);

            const completerName = activity.completerFullName || activity.completerUserId;
            const assigneeName = activity.assigneeFullName || activity.assignee;
            const responsibleName = isActive ? assigneeName : completerName || assigneeName;
            const responsibleLabel = !isActive && completerName
              ? t("processes.completedBy")
              : t("processes.assignedTo");
            const systemExecutionLabel = AUTOMATED_TASK_TYPES.has(activity.activityType)
              ? t("processes.timelineExecutedAutomatically")
              : EVENT_TYPES.has(activity.activityType)
                ? t("processes.timelineProcessEvent")
                : null;

            const isStartEvent = activity.activityType === 'startEvent';
            const isEndEvent = activity.activityType === 'endEvent';
            const isManualTermination = activity.activityType === MANUAL_TERMINATION_TYPE;
            const terminatedByName = activity.terminatedByFullName || activity.terminatedByUserId;

            const showFullDate = isStartEvent || isEndEvent || isManualTermination;

            return (
              <TimelineItem key={activity.id} data-testid={`timeline-item-${activity.id}`}>
                <TimelineOppositeContent sx={{ m: 'auto 0', px: isMobile ? 1 : 2, py: 2 }} align="right" color="text.secondary">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {showFullDate ? (
                      <>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                          {start.date.toUpperCase()}
                        </Typography>
                        <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
                          {start.time}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                          {start.date.toUpperCase()}
                        </Typography>
                        <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
                          {start.time}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, color: isActive ? 'primary.main' : 'text.secondary' }}>
                          {isActive && <AccessTime sx={{ fontSize: 12 }} />}
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            {isActive ? t("processes.timeAgo") : ""}{duration}
                          </Typography>
                        </Box>
                      </>
                    )}

                    {isEndEvent && activity.endTime && (
                      <>
                        <Box sx={{ height: 8, width: 1, bgcolor: 'divider', my: 0.5 }} />
                        <Typography variant="caption" sx={{ fontWeight: 'bold', lineHeight: 1.2, color: 'success.main' }}>
                          {end.time}
                        </Typography>
                      </>
                    )}
                  </Box>
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineConnector sx={{ bgcolor: index === 0 ? 'transparent' : 'divider' }} />
                  <TimelineDot
                    color={getActivityColor(activity.activityType, activity.endTime) as any}
                    sx={{
                      p: isMobile ? 0.5 : 1,
                      my: 0.5,
                      boxShadow: isActive ? '0 0 0 4px rgba(25, 118, 210, 0.2)' : undefined
                    }}
                  >
                    {getActivityIcon(activity.activityType)}
                  </TimelineDot>
                  <TimelineConnector sx={{ bgcolor: isLast ? 'transparent' : 'divider' }} />
                </TimelineSeparator>
                <TimelineContent sx={{ py: 2, px: isMobile ? 1 : 2 }}>
                  <Paper
                    elevation={0}
                    variant="outlined"
                    sx={{
                      p: isMobile ? 1.5 : 2,
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      borderLeft: 3,
                      borderColor: `${getActivityColor(activity.activityType, activity.endTime)}.main`,
                      minWidth: 0,
                      ...(isActive && {
                        borderColor: 'primary.main',
                        bgcolor: 'action.hover'
                      })
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ lineHeight: 1.2, mb: 0.5 }}>
                      {isManualTermination
                        ? t("processes.timelineManualTermination")
                        : activity.activityName || activity.activityType}
                    </Typography>

                    {activity.activityType === 'userTask' && responsibleName && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Person sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.primary" noWrap>
                          <Box component="span" color="text.secondary">
                            {responsibleLabel}:{' '}
                          </Box>
                          {responsibleName}
                        </Typography>
                      </Box>
                    )}

                    {systemExecutionLabel && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Settings sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {systemExecutionLabel}
                        </Typography>
                      </Box>
                    )}

                    {isManualTermination && terminatedByName && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Person sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.primary" noWrap>
                          <Box component="span" color="text.secondary">
                            {t("processes.terminatedBy")}:{' '}
                          </Box>
                          {terminatedByName}
                        </Typography>
                      </Box>
                    )}

                    {isManualTermination && activity.terminationJustification && (
                      <Typography variant="caption" color="text.primary" sx={{ display: 'block', mt: 0.5 }}>
                        <Box component="span" color="text.secondary">
                          {t("processes.terminationJustification")}:{' '}
                        </Box>
                        {activity.terminationJustification}
                      </Typography>
                    )}

                    {isActive && (
                      <Chip label={t("processes.history.inProgress")} size="small" color="primary" variant="outlined" sx={{ mt: 1, height: 20, fontSize: '0.65rem' }} />
                    )}
                  </Paper>
                </TimelineContent>
              </TimelineItem>
            );
          })}
        </Timeline>
      )}
    </Box>
  );
}
